/**
 * @file     index.tsx
 * @purpose  Blog index on the hub shell: H1 and subhead, category links as
 *           real links, the lead post laid out wide, then the article grid and
 *           older/newer pagination. Every link is in the server HTML.
 */
'use client';

import Link from 'next/link';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { leadFlowRoutes } from '@/core/constants';
import { useAnalytics } from '@/hooks/useAnalytics';
import { focusRingSx, hairline, hubColors, hubRadius, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';
import { SectionHeader } from '@/components/hub/SectionHeader';
import { LocalBlogCard } from '@/features/blog/components/LocalBlogCard/LocalBlogCard';
import type { BlogCategory, LocalBlogPost } from '@/features/blog/types/LocalBlogPost';
import { SeoHubShell } from '@/views/seo-hub/SeoHubShell';
import { useBlogContent } from './pageContent';
import { blogIndexHref } from './blogIndexQuery';

type FilterKey = 'all' | 'marketUpdate' | 'buyerGuide' | 'sellerGuide' | 'presale' | 'neighbourhood';

const CATEGORY_FILTERS: { i18nField: FilterKey; value: BlogCategory | 'all' }[] = [
  { i18nField: 'all', value: 'all' },
  { i18nField: 'marketUpdate', value: 'market-update' },
  { i18nField: 'buyerGuide', value: 'buyer-guide' },
  { i18nField: 'sellerGuide', value: 'seller-guide' },
  { i18nField: 'presale', value: 'presale' },
  { i18nField: 'neighbourhood', value: 'neighbourhood' },
];

type BlogPageProps = {
  /** One server-loaded page of posts for the active category. */
  posts: LocalBlogPost[];
  activeCategory: BlogCategory | 'all';
  page: number;
  hasMore: boolean;
};

const pagerLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  px: 4,
  borderRadius: `${hubRadius.control}px`,
  border: `1px solid ${hubColors.borderStrong}`,
  color: hubColors.textPrimary,
  fontSize: 15,
  fontWeight: 500,
  textDecoration: 'none',
  transition: transition('border-color', 'background-color'),
  '&:hover': { borderColor: hubColors.interactivePrimary, backgroundColor: hubColors.surfaceSunken },
  ...focusRingSx,
  ...reducedMotionSx,
} as const;

const BlogPage = ({ posts, activeCategory, page, hasMore }: BlogPageProps) => {
  const { trackCtaClick } = useAnalytics();
  const { t } = useTranslation();
  const content = useBlogContent();

  const [lead, ...rest] = posts;
  const showLead = page === 1 && lead != null;
  const grid = showLead ? rest : posts;

  return (
    <SeoHubShell
      eyebrow={content.hero.eyebrow}
      heading={content.hero.headline}
      intro={content.hero.description}
      crumbs={[{ label: content.crumbs.home, href: '/' }, { label: content.crumbs.blog }]}
      provenance={{ source: content.provenance, mlsFootnote: false }}
    >
      <Box
        component="nav"
        aria-label={content.categoriesLabel}
        sx={{ mt: { xs: -2, md: -4 }, mb: { xs: 4, md: 6 }, borderBottom: hairline }}
      >
        <Box
          component="ul"
          sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', columnGap: { xs: 3, md: 4 } }}
        >
          {CATEGORY_FILTERS.map((cat) => {
            const isActive = activeCategory === cat.value;
            return (
              <li key={cat.value}>
                <Box
                  component={Link}
                  href={blogIndexHref(cat.value)}
                  scroll={false}
                  aria-current={isActive ? 'page' : undefined}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    minHeight: 48,
                    fontSize: 15,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? hubColors.textPrimary : hubColors.textMuted,
                    textDecoration: 'none',
                    boxShadow: `inset 0 -2px 0 ${isActive ? hubColors.interactivePrimary : 'transparent'}`,
                    transition: transition('color', 'box-shadow'),
                    '&:hover': { color: hubColors.textPrimary },
                    ...focusRingSx,
                    ...reducedMotionSx,
                  }}
                >
                  {content.filters[cat.i18nField]}
                </Box>
              </li>
            );
          })}
        </Box>
      </Box>

      {posts.length === 0 ? (
        <Box
          role="status"
          sx={{ py: { xs: 6, md: 8 }, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, borderBottom: hairline }}
        >
          <Box component="p" sx={{ m: 0, fontSize: 17, color: hubColors.textSecondary }}>{content.emptyState}</Box>
          {activeCategory !== 'all' ? (
            <Box component={Link} href={blogIndexHref('all')} sx={pagerLinkSx}>{content.emptyAction}</Box>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 8 } }}>
          {showLead ? <LocalBlogCard post={lead} featured priority locationTag="blog_index_featured" /> : null}
          {grid.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                columnGap: { xs: 3, md: 4 },
                rowGap: { xs: 5, md: 6 },
              }}
            >
              {grid.map((post, index) => (
                <LocalBlogCard key={post.id} post={post} locationTag="blog_index_grid" priority={!showLead && page === 1 && index < 3} />
              ))}
            </Box>
          ) : null}
        </Box>
      )}

      {(page > 1 || hasMore) && (
        <Box
          component="nav"
          aria-label={t('pages.blog.pagination.label', { defaultValue: 'More articles' })}
          sx={{
            mt: { xs: 6, md: 8 },
            pt: 3,
            borderTop: hairline,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {page > 1 ? (
            <Box component={Link} href={blogIndexHref(activeCategory, page - 1)} sx={pagerLinkSx}>
              {t('pages.blog.pagination.newer', { defaultValue: 'Newer articles' })}
            </Box>
          ) : <span />}
          <Box component="p" sx={{ m: 0, fontSize: 14, color: hubColors.textMuted }}>
            {t('pages.blog.pagination.page', { defaultValue: 'Page {{page}}', page })}
          </Box>
          {hasMore ? (
            <Box component={Link} href={blogIndexHref(activeCategory, page + 1)} sx={pagerLinkSx}>
              {t('pages.blog.pagination.older', { defaultValue: 'Older articles' })}
            </Box>
          ) : <span />}
        </Box>
      )}

      <Box
        sx={{
          mt: { xs: 7, md: 10 },
          p: { xs: 4, md: 6 },
          borderRadius: `${hubRadius.panel}px`,
          backgroundColor: hubColors.surfaceSunken,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
        }}
      >
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <SectionHeader title={content.crossSell.title} description={content.crossSell.description} size="compact" />
        </Box>
        <Box
          component={Link}
          href={leadFlowRoutes.chooser}
          onClick={() => trackCtaClick(content.hero.startHere, 'blog_index_crosssell', leadFlowRoutes.chooser)}
          sx={{ ...pagerLinkSx, minHeight: 52, px: 3, backgroundColor: hubColors.interactivePrimary, borderColor: hubColors.interactivePrimary, color: hubColors.textOnPrimary, '&:hover': { backgroundColor: hubColors.primaryHover, borderColor: hubColors.primaryHover } }}
        >
          {content.hero.startHere}
        </Box>
      </Box>
    </SeoHubShell>
  );
};

export default BlogPage;
